import { MAX_TICKS_PER_FRAME, TICK_DT, TICK_MS } from "./constants";

/**
 * The closed-loop input lead controller (`gr_core::lead::LeadControl`, run in the browser through
 * `gr-wasm`; `gr-bot` runs the same code natively). Fed with every snapshot; it decides how fast
 * the prediction clock runs and when the tick numbering jumps.
 */
export interface LeadController {
  /** A new life starts: forget the alignment, keep the clock rate and the measured jitter. */
  restart(): void;
  /** The clock rate (server ticks per client tick) from the ping/pong clock. */
  seedRate(rate: number): void;
  /** Returns the shift to apply to the tick numbering: 0 = none, positive = forward. */
  onSnapshot(lead: number, slack: number | null, frameTicks: number): number;
  timeScale(): number;
  /** Ticks the inputs should arrive ahead of the server's need (what a respawn adds). */
  target(): number;
  /** Smoothed arrival margin of the inputs, ticks. */
  meanSlack(): number;
}

/** Weight of a frame shorter than the smoothed interval; a longer one (a stall) weighs a tenth of it, so hitches do not read as a slower display. */
const FRAME_WEIGHT = 0.05;
/** Frames longer than this (ms) are stalls, not the display rate. */
const MAX_FRAME_MS = 100;
/** Frame interval assumed until frames were measured: a 60 Hz display (`gr_core::lead` `SEED_FRAME_TICKS`). */
const SEED_FRAME_MS = 1000 / 60;
/** Tick start times kept for `tickStartMs`: only the batch of the last frame is read, so it is sized in ticks per frame (twice `MAX_TICKS_PER_FRAME`). */
const START_RING = 2 * MAX_TICKS_PER_FRAME;

/**
 * Prediction clock: fixed `TICK_DT` (1/120 s) ticks, run slightly faster or slower than real time
 * so each input arrives at the server just before it is needed (docs/NETCODE.md "Clock and tick
 * synchronization").
 *
 * Per frame call `advance(now)`; it returns how many ticks to simulate. Those ticks are
 * `batchFirstTick .. batchFirstTick + n - 1` (and `nextTick()` is `batchFirstTick + n` afterwards).
 * With every snapshot call `onSnapshot(snapshot.inputLead, snapshot.inputSlack)`: the controller
 * returns the clock speed (`timeScale`) and, when the clock is far off, a jump of the tick
 * numbering (`onResync` fires; ticks skipped this way are filled in silently by `Predictor.step`,
 * like the server fills inputs it never got). More than `MAX_TICKS_PER_FRAME` (133 ms) of ticks due
 * in one frame (a throttled tab) is a resync too.
 */
export class TickClock {
  /** Number of hard resyncs so far. */
  resyncCount = 0;
  /** Called after a hard resync with the new `nextTick()`. */
  onResync: ((nextTick: number) => void) | null = null;
  /** First tick of the batch the last `advance` returned. */
  batchFirstTick = 0;

  private ctl: LeadController | null = null;
  private next = 0;
  /** Seconds of simulated time accumulated toward the next tick. */
  private acc = 0;
  private lastMs = 0;
  private started = false;
  /** Smoothed frame interval (ms). */
  private frameMs = SEED_FRAME_MS;
  private readonly starts = new Float64Array(START_RING);

  /** `makeController` builds the controller when it is first needed (the wasm module may not be loaded yet). */
  constructor(private readonly makeController: () => LeadController) {}

  private controller(): LeadController {
    return (this.ctl ??= this.makeController());
  }

  /** A new connection: the clock rate and jitter learned on the old one do not apply. */
  newConnection(): void {
    this.ctl = null;
    this.started = false;
  }

  /** The ping/pong clock measured the server's tick rate against ours (`ConnectionClock.rate`). */
  seedRate(rate: number): void {
    this.controller().seedRate(rate);
  }

  /** Clock speed relative to real time. */
  get timeScale(): number {
    return this.ctl?.timeScale() ?? 1;
  }

  /**
   * How many ticks ahead of the server's clock the first tick of a life is numbered. A tick is
   * simulated, and its input sent, at the END of its interval, a tick after it began, and the
   * frame that does it comes up to a frame later: aiming the mean arrival margin at the
   * controller's target means adding both to it. (Without them every life started late by about
   * a tick and a half: 45 % of its first 30 inputs missed the server's deadline.)
   */
  firstTickLead(): number {
    return this.controller().target() + 1 + this.frameMs / TICK_MS / 2;
  }

  /** Smoothed arrival margin of the inputs, ticks (0 before the first one). */
  meanSlack(): number {
    return this.ctl?.meanSlack() ?? 0;
  }

  /** The first tick to simulate is `firstTick`; its interval begins at `nowMs`. */
  start(firstTick: number, nowMs: number): void {
    this.controller().restart();
    this.next = firstTick;
    this.batchFirstTick = firstTick;
    this.acc = 0;
    this.lastMs = nowMs;
    this.started = true;
    this.starts.fill(nowMs);
  }

  /** Ticks to simulate this frame (0..`MAX_TICKS_PER_FRAME`). */
  advance(nowMs: number): number {
    if (!this.started) return 0;
    const dtMs = Math.max(0, nowMs - this.lastMs);
    this.lastMs = nowMs;
    if (dtMs > 0 && dtMs <= MAX_FRAME_MS) this.frameMs += (dtMs - this.frameMs) * (dtMs < this.frameMs ? FRAME_WEIGHT : FRAME_WEIGHT / 10);
    this.acc += (dtMs / 1000) * this.timeScale;
    this.batchFirstTick = this.next;
    const pending = Math.floor(this.acc / TICK_DT + 1e-9);
    if (pending === 0) return 0;
    this.acc = Math.max(0, this.acc - pending * TICK_DT);

    let run = pending;
    const overflow = pending > MAX_TICKS_PER_FRAME;
    if (overflow) {
      // Fast-forward over the ticks that cannot be run; the predictor fills them in silently.
      this.next += pending - MAX_TICKS_PER_FRAME;
      this.batchFirstTick = this.next;
      run = MAX_TICKS_PER_FRAME;
    }
    const tickMs = this.tickDurationMs();
    const accMs = (this.acc * 1000) / this.timeScale;
    for (let j = 0; j < run; j++) {
      const end = nowMs - accMs - (run - 1 - j) * tickMs;
      this.starts[(this.next + j) % START_RING] = end - tickMs;
    }
    this.next += run;
    if (overflow) this.resync();
    return run;
  }

  /** The tick the next simulated input is for (the one after the last batch). */
  nextTick(): number {
    return this.next;
  }

  /** Position inside the tick being accumulated, 0..1, for render interpolation. */
  alpha(nowMs: number): number {
    const extra = Math.max(0, nowMs - this.lastMs) / 1000;
    const a = (this.acc + extra * this.timeScale) / TICK_DT;
    return a < 0 ? 0 : a > 1 ? 1 : a;
  }

  /** Real duration of one tick at the current speed. */
  tickDurationMs(): number {
    return TICK_MS / this.timeScale;
  }

  /** Local time at which `tick` began (for the sub-tick position of a click). */
  tickStartMs(tick: number): number {
    if (tick < this.next && tick >= this.next - START_RING) return this.starts[tick % START_RING] as number;
    const currentStart = this.lastMs - (this.acc * 1000) / this.timeScale;
    return currentStart + (tick - this.next) * this.tickDurationMs();
  }

  /**
   * A snapshot arrived: `lead` is its `inputLead`, `slack` its `inputSlack` (1/960 s,
   * `SLACK_PER_TICK` per tick; null when no input arrived since the previous snapshot).
   */
  onSnapshot(lead: number, slack: number | null): void {
    if (!this.started) return;
    const shift = this.controller().onSnapshot(lead, slack, this.frameMs / TICK_MS);
    if (shift !== 0) {
      // The server sees our inputs `shift` ticks too late (positive) or too early: shift the numbering.
      this.next = Math.max(1, this.next + shift);
      this.batchFirstTick = this.next;
      this.resync();
    }
  }

  private resync(): void {
    this.resyncCount++;
    this.onResync?.(this.next);
  }
}
